import { coordinatePair, wgs84ToGcj02 } from './coordinates';

let sdkPromise;
const SDK_TIMEOUT = 15000;

// 安全密钥只由 Nginx 附加。这里仅配置同源代理与公开的 JS API Key。
export const loadAMap = () => {
  if (sdkPromise) return sdkPromise;
  const key = process.env.REACT_APP_AMAP_KEY?.trim();
  if (!key) return Promise.reject(new Error('地图暂未配置，可手动填写位置'));
  window._AMapSecurityConfig = { serviceHost: `${window.location.origin}/_AMapService` };
  sdkPromise = new Promise((resolve, reject) => {
    if (window.AMap?.Map && window.AMap?.PlaceSearch && window.AMap?.Geocoder) { resolve(window.AMap); return; }
    const script = document.createElement('script');
    const callback = `__ipartnerAMapReady${Date.now()}`;
    let finished = false;
    const finish = error => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      delete window[callback];
      script.onerror = null;
      if (error) { script.remove(); reject(error); }
      else resolve(window.AMap);
    };
    const timer = setTimeout(() => finish(new Error('地图加载超时，请重试或手动填写位置')), SDK_TIMEOUT);
    window[callback] = () => finish(window.AMap?.Map && window.AMap?.PlaceSearch && window.AMap?.Geocoder ? null : new Error('地图加载失败，请重试'));
    script.async = true;
    script.src = `https://webapi.amap.com/maps?${new URLSearchParams({ v: '2.0', key, plugin: 'AMap.PlaceSearch,AMap.Geocoder', callback })}`;
    script.onerror = () => finish(new Error('地图加载失败，请检查网络后重试'));
    document.head.appendChild(script);
  }).catch(error => { sdkPromise = undefined; throw error; });
  return sdkPromise;
};

export const locateBrowser = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) { reject(new Error('浏览器不支持定位，请搜索地点或手动选点')); return; }
  navigator.geolocation.getCurrentPosition(result => resolve([result.coords.longitude, result.coords.latitude]), error => {
    reject(new Error(error.code === 1 ? '定位权限未开启，请搜索地点或手动选点' : '暂时无法获取位置，请搜索地点或手动选点'));
  }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
});

const serviceCall = (run, fallback) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('地点查询超时，请重试')), SDK_TIMEOUT);
  try {
    run((status, result) => {
      clearTimeout(timer);
      if (status === 'complete') resolve(result);
      else if (status === 'no_data') resolve(null);
      else reject(new Error(fallback));
    });
  } catch (error) { clearTimeout(timer); reject(error); }
});

const readPlaces = result => (result?.poiList?.pois || []).map(poi => {
  const point = coordinatePair(poi.location?.getLng?.(), poi.location?.getLat?.());
  return point ? {
    id: poi.id, name: poi.name,
    address: [poi.pname, poi.cityname, poi.adname, typeof poi.address === 'string' ? poi.address : ''].filter(Boolean).join(' '),
    point,
  } : null;
}).filter(Boolean);

const distanceFrom = (point, origin) => {
  // Haversine 项随球面距离单调增加，可直接比较以避免额外开方。
  const radians = Math.PI / 180;
  const latitudeDelta = (point[1] - origin[1]) * radians;
  const longitudeDelta = (point[0] - origin[0]) * radians;
  return Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(origin[1] * radians) * Math.cos(point[1] * radians) * Math.sin(longitudeDelta / 2) ** 2;
};

// currentPosition 只接受设备定位的 WGS84 坐标，不使用帖子的已选活动地点。
export const searchMapPlaces = async (keyword, currentPosition) => {
  const AMap = await loadAMap();
  const position = coordinatePair(currentPosition?.[0], currentPosition?.[1])
    || await locateBrowser().then(point => coordinatePair(point?.[0], point?.[1])).catch(() => null);
  const origin = position && wgs84ToGcj02(position);
  const service = new AMap.PlaceSearch({ pageSize: origin ? 25 : 6, extensions: 'base' });
  let places = [];
  if (origin) {
    try {
      places = readPlaces(await serviceCall(callback => service.searchNearBy(keyword, origin, 50000, callback), '附近地点搜索失败'));
    } catch { /* 周边服务不可用时保留普通关键词搜索。 */ }
  }
  if (!places.length) places = readPlaces(await serviceCall(callback => service.search(keyword, callback), '地点搜索失败，请稍后重试'));
  if (origin) places.sort((left, right) => distanceFrom(left.point, origin) - distanceFrom(right.point, origin));
  return places.slice(0, 6);
};

export const reverseMapAddress = async point => {
  const AMap = await loadAMap();
  const result = await serviceCall(callback => new AMap.Geocoder({ extensions: 'base' }).getAddress(point, callback), '地点名称暂时无法获取');
  return result?.regeocode?.formattedAddress || '';
};
